import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { FormNavigation } from '../components/form/FormNavigation';
import { TextField } from '../components/fields/TextField';
import { RadioGroup } from '../components/fields/RadioGroup';
import { SelectField } from '../components/fields/SelectField';
import { PhoneInput } from '../components/fields/PhoneInput';
import { CityAutocomplete } from '../components/fields/CityAutocomplete';
import { NameInput } from '../components/fields/NameInput';
import { EmailInput } from '../components/fields/EmailInput';
import { CurrencyInput } from '../components/fields/CurrencyInput';
import { useFormStore, useNavigation } from '../store/formStore';
import { getCountryOptions } from '../config/countries';

// Schéma de validation pour le co-emprunteur
const coBorrowerSchema = z.object({
  // État civil
  coBorrowerTitle: z.enum(['M', 'MME'], {
    errorMap: () => ({ message: 'Veuillez sélectionner un titre' })
  }),
  coBorrowerFirstName: z.string()
    .min(2, 'Le prénom doit contenir au moins 2 caractères')
    .max(50, 'Le prénom ne peut pas dépasser 50 caractères')
    .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, 'Le prénom contient des caractères non autorisés'),
  coBorrowerLastName: z.string()
    .min(2, 'Le nom doit contenir au moins 2 caractères')
    .max(50, 'Le nom ne peut pas dépasser 50 caractères')
    .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, 'Le nom contient des caractères non autorisés'),
  coBorrowerBirthName: z.string().optional().or(z.literal('')),
  coBorrowerBirthDate: z.string()
    .min(1, 'La date de naissance est requise')
    .refine((date) => {
      const birthDate = new Date(date);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age >= 18 && age <= 100;
    }, 'Le co-emprunteur doit avoir entre 18 et 100 ans'),
  coBorrowerBirthCity: z.string().optional().or(z.literal('')),
  coBorrowerBirthDepartment: z.string().optional().or(z.literal('')),
  coBorrowerBirthCountry: z.string().default('FR'),
  coBorrowerNationality: z.string().default('FR'),

  // Contact
  coBorrowerEmail: z.string()
    .email('Veuillez saisir un email valide')
    .regex(/^[^+]+@[^+]+$/, 'Les alias email avec "+" ne sont pas acceptés'),
  coBorrowerPhone: z.string()
    .min(1, 'Le numéro de téléphone est requis')
    .regex(/^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/, 'Numéro de téléphone français invalide'),

  // Revenus
  coBorrowerMonthlySalary: z.preprocess(
    (val) => (val === '' || val === null || val === undefined || val === 0 || (typeof val === 'number' && isNaN(val)) ? undefined : val),
    z.number()
      .min(1, 'Le salaire doit être supérieur à 0')
      .optional()
  ),
  coBorrowerRetirementPension: z.number().min(0).optional().or(z.literal(0)),
  coBorrowerRetirementPensionPeriodicity: z.string().optional().or(z.literal('')),
  coBorrowerDisabilityPension: z.number().min(0).optional().or(z.literal(0)),
  coBorrowerDisabilityPensionPeriodicity: z.string().optional().or(z.literal('')),
  coBorrowerReversionaryPension: z.number().min(0).optional().or(z.literal(0)),
  coBorrowerReversionaryPensionPeriodicity: z.string().optional().or(z.literal('')),
  coBorrowerOtherIncome: z.number().min(0).optional().or(z.literal(0)),

  // Situation professionnelle
  coBorrowerProfessionCode: z.string().min(1, 'Veuillez sélectionner la situation professionnelle'),
  coBorrowerContractTypeCode: z.string().optional().or(z.literal('')),
  coBorrowerContractEndDate: z.string().optional().or(z.literal('')),
  coBorrowerCompanyName: z.string().optional().or(z.literal('')),
  coBorrowerSeniorityMonths: z.number().min(0).max(720).optional().or(z.literal(0)),
}).refine((data) => {
  // Validation conditionnelle: date de fin de contrat requise pour CDD et Intérim
  if (['CDD', 'INT'].includes(data.coBorrowerContractTypeCode || '')) {
    return data.coBorrowerContractEndDate && data.coBorrowerContractEndDate.length > 0;
  }
  return true;
}, {
  message: 'La date de fin de contrat est requise pour les CDD et Intérim',
  path: ['coBorrowerContractEndDate']
}).refine((data) => {
  // Validation: la date de fin de contrat doit être dans le futur
  if (data.coBorrowerContractEndDate && data.coBorrowerContractEndDate.length > 0) {
    const endDate = new Date(data.coBorrowerContractEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return endDate > today;
  }
  return true;
}, {
  message: 'La date de fin de contrat doit être dans le futur',
  path: ['coBorrowerContractEndDate']
}).superRefine((data, ctx) => {
  if (data.coBorrowerBirthCountry === 'FR') {
    if (!data.coBorrowerBirthCity || data.coBorrowerBirthCity.length < 2 || data.coBorrowerBirthDepartment === '99') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Veuillez sélectionner votre ville de naissance dans la liste', path: ['coBorrowerBirthCity'] });
    }
    if (!data.coBorrowerBirthDepartment || !/^([0-9]{2,3})$/.test(data.coBorrowerBirthDepartment)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Le département est invalide', path: ['coBorrowerBirthDepartment'] });
    }
  }
  // Salaire requis uniquement pour les actifs (professionCode <= 6300)
  const codeNum = parseInt(data.coBorrowerProfessionCode || '0', 10);
  if (codeNum > 0 && codeNum <= 6300) {
    const salary = data.coBorrowerMonthlySalary;
    if (salary === undefined || salary === null || salary < 1) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Veuillez indiquer les revenus mensuels', path: ['coBorrowerMonthlySalary'] });
    }
  }
});

interface CoBorrowerFormData {
  coBorrowerTitle: 'M' | 'MME';
  coBorrowerFirstName: string;
  coBorrowerLastName: string;
  coBorrowerBirthName?: string;
  coBorrowerBirthDate: string;
  coBorrowerBirthCity: string;
  coBorrowerBirthDepartment: string;
  coBorrowerBirthCountry: string;
  coBorrowerNationality: string;
  coBorrowerEmail: string;
  coBorrowerPhone?: string;
  coBorrowerMonthlySalary: number;
  coBorrowerRetirementPension?: number;
  coBorrowerRetirementPensionPeriodicity?: string;
  coBorrowerDisabilityPension?: number;
  coBorrowerDisabilityPensionPeriodicity?: string;
  coBorrowerReversionaryPension?: number;
  coBorrowerReversionaryPensionPeriodicity?: string;
  coBorrowerOtherIncome?: number;
  coBorrowerProfessionCode: string;
  coBorrowerContractTypeCode?: string;
  coBorrowerContractEndDate?: string;
  coBorrowerCompanyName?: string;
  coBorrowerCompanyCity?: string;
  coBorrowerSeniorityMonths?: number;
}

export function CoBorrowerStep() {
  const { formData, updateField } = useFormStore();
  const { goToPreviousStep, goToNextStep, canGoBack } = useNavigation();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors: formErrors, isValid, touchedFields }
  } = useForm<CoBorrowerFormData>({
    resolver: zodResolver(coBorrowerSchema),
    defaultValues: {
      coBorrowerTitle: formData.coBorrowerTitle || undefined,
      coBorrowerFirstName: formData.coBorrowerFirstName || '',
      coBorrowerLastName: formData.coBorrowerLastName || '',
      coBorrowerBirthName: formData.coBorrowerBirthName || '',
      coBorrowerBirthDate: formData.coBorrowerBirthDate || '',
      coBorrowerBirthCity: formData.coBorrowerBirthCity || '',
      coBorrowerBirthDepartment: formData.coBorrowerBirthDepartment || '',
      coBorrowerBirthCountry: formData.coBorrowerBirthCountry || 'FR',
      coBorrowerNationality: formData.coBorrowerNationality || 'FR',
      coBorrowerEmail: formData.coBorrowerEmail || '',
      coBorrowerPhone: formData.coBorrowerPhone || '',
      coBorrowerMonthlySalary: formData.coBorrowerMonthlySalary || 0,
      coBorrowerRetirementPension: formData.coBorrowerRetirementPension || 0,
      coBorrowerRetirementPensionPeriodicity: formData.coBorrowerRetirementPensionPeriodicity || 'M',
      coBorrowerDisabilityPension: formData.coBorrowerDisabilityPension || 0,
      coBorrowerDisabilityPensionPeriodicity: formData.coBorrowerDisabilityPensionPeriodicity || 'M',
      coBorrowerReversionaryPension: formData.coBorrowerReversionaryPension || 0,
      coBorrowerReversionaryPensionPeriodicity: formData.coBorrowerReversionaryPensionPeriodicity || 'M',
      coBorrowerOtherIncome: formData.coBorrowerOtherIncome || 0,
      coBorrowerProfessionCode: formData.coBorrowerProfessionCode || '',
      coBorrowerContractTypeCode: formData.coBorrowerContractTypeCode || 'CDI',
      coBorrowerContractEndDate: formData.coBorrowerContractEndDate || '',
      coBorrowerCompanyName: formData.coBorrowerCompanyName || '',
      coBorrowerCompanyCity: formData.coBorrowerCompanyCity || '',
      coBorrowerSeniorityMonths: formData.coBorrowerSeniorityMonths || ('' as unknown as number)
    },
    mode: 'onChange'
  });

  useEffect(() => { trigger(); }, []);

  const fieldError = (name: keyof CoBorrowerFormData) =>
    touchedFields[name] ? formErrors[name]?.message : undefined;

  const touchField = (name: keyof CoBorrowerFormData) => () =>
    setValue(name, watch(name), { shouldTouch: true, shouldValidate: true });

  const [coBorrowerBirthCityConfirmed, setCoBorrowerBirthCityConfirmed] = useState(
    !!(formData.coBorrowerBirthCity && formData.coBorrowerBirthDepartment && formData.coBorrowerBirthCityVerified)
  );
  const [coBorrowerCompanyCityConfirmed, setCoBorrowerCompanyCityConfirmed] = useState(
    !!(formData.coBorrowerCompanyCity)
  );
  const [isCoBorrowerBirthCountryFR, setIsCoBorrowerBirthCountryFR] = useState(
    (formData.coBorrowerBirthCountry || 'FR') === 'FR'
  );

  const watchedProfessionCodeNum = parseInt(String(formData.coBorrowerProfessionCode ?? '0'), 10) || 0;
  const watchedContractTypeCode = watch('coBorrowerContractTypeCode') || 'CDI';

  // Cap ancienneté : le co-emprunteur ne peut pas avoir commencé avant ses 16 ans
  const coBorrowerBirthDate = formData.coBorrowerBirthDate ? new Date(formData.coBorrowerBirthDate) : null;
  const coBorrowerAgeInMonths = coBorrowerBirthDate
    ? (new Date().getFullYear() - coBorrowerBirthDate.getFullYear()) * 12 + (new Date().getMonth() - coBorrowerBirthDate.getMonth())
    : 720;
  const maxCoBorrowerSeniorityMonths = Math.max(0, coBorrowerAgeInMonths - 16 * 12);
  const watchedCoBorrowerSeniorityMonths = watch('coBorrowerSeniorityMonths');
  const watchedCoBorrowerMonthlySalary = watch('coBorrowerMonthlySalary');
  const watchedCoBorrowerRetirementPension = watch('coBorrowerRetirementPension');
  const watchedCoBorrowerDisabilityPension = watch('coBorrowerDisabilityPension');
  const watchedCoBorrowerReversionaryPension = watch('coBorrowerReversionaryPension');
  const watchedCoBorrowerOtherIncome = watch('coBorrowerOtherIncome');

  const titleOptions = [
    { value: 'M', label: 'Monsieur' },
    { value: 'MME', label: 'Madame' }
  ];

  const contractTypeOptions = [
    { value: 'CDI', label: 'CDI' },
    { value: 'CDD', label: 'CDD' },
    { value: 'INT', label: 'Intérim' },
    { value: 'AUT', label: 'Autre' },
  ];

  // Actif = professionCode entre 1100 et 6300
  const isActiveProfession = watchedProfessionCodeNum > 0 && watchedProfessionCodeNum < 7700;
  // Employeur uniquement si salarié : professionCode entre 3300 et 6300 (spec p.30)
  const canHaveEmployer = watchedProfessionCodeNum >= 3300 && watchedProfessionCodeNum <= 6300;
  // Type de contrat uniquement si salarié : professionCode entre 3300 et 6300 (spec p.41)
  const needsContractType = watchedProfessionCodeNum >= 3300 && watchedProfessionCodeNum <= 6300;

  const onSubmit = (data: CoBorrowerFormData) => {
    Object.entries(data).forEach(([key, value]) => {
      updateField(key, value);
    });
    goToNextStep();
  };

  const handleFieldChange = (fieldName: keyof CoBorrowerFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const numericFields = ['coBorrowerMonthlySalary', 'coBorrowerRetirementPension', 'coBorrowerDisabilityPension', 'coBorrowerReversionaryPension', 'coBorrowerOtherIncome', 'coBorrowerSeniorityMonths'];
      const value = numericFields.includes(fieldName)
        ? (e.target.value === '' ? ('' as unknown as number) : Number(e.target.value))
        : e.target.value;
      setValue(fieldName, value, { shouldValidate: true });
      updateField(fieldName, value);
    };

  const handleStatusChange = (value: string) => {
    setValue('coBorrowerTitle', value as 'M' | 'MME', { shouldValidate: true, shouldTouch: true });
    updateField('coBorrowerTitle', value);
  };

  const handleProfessionCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setValue('coBorrowerProfessionCode', value, { shouldValidate: true, shouldTouch: true });
    updateField('coBorrowerProfessionCode', value);
    const codeNum = parseInt(value || '0', 10);
    if (codeNum >= 3300 && codeNum <= 6300) {
      setValue('coBorrowerContractTypeCode', 'CDI', { shouldValidate: true });
      updateField('coBorrowerContractTypeCode', 'CDI');
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Informations du co-emprunteur</CardTitle>
          <CardDescription>
            Nous avons besoin des informations de votre conjoint(e) / partenaire de PACS
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">

            {/* SECTION 1: État civil */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-900 border-b pb-2">
                État civil
              </h3>

              <RadioGroup
                label="Titre"
                options={titleOptions}
                value={watch('coBorrowerTitle')}
                onChange={handleStatusChange}
                name="coBorrowerTitle"
                required
                error={fieldError('coBorrowerTitle')}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <NameInput
                  value={formData.coBorrowerFirstName || ''}
                  onChange={(e) => {
                    setValue('coBorrowerFirstName', e.target.value, { shouldValidate: true });
                    updateField('coBorrowerFirstName', e.target.value);
                  }}
                  onBlur={touchField('coBorrowerFirstName')}
                  label="Prénom"
                  type="firstName"
                  placeholder="Prénom du co-emprunteur"
                  required
                  error={fieldError('coBorrowerFirstName')}
                />

                <NameInput
                  value={formData.coBorrowerLastName || ''}
                  onChange={(e) => {
                    setValue('coBorrowerLastName', e.target.value, { shouldValidate: true });
                    updateField('coBorrowerLastName', e.target.value);
                  }}
                  onBlur={touchField('coBorrowerLastName')}
                  label="Nom de famille"
                  type="lastName"
                  placeholder="Nom du co-emprunteur"
                  required
                  error={fieldError('coBorrowerLastName')}
                />
              </div>

              <NameInput
                value={formData.coBorrowerBirthName || ''}
                onChange={(e) => {
                  setValue('coBorrowerBirthName', e.target.value, { shouldValidate: true });
                  updateField('coBorrowerBirthName', e.target.value);
                }}
                label="Nom de naissance (si différent)"
                placeholder="Nom de naissance"
                type="lastName"
                error={fieldError('coBorrowerBirthName')}
              />

              <TextField
                {...register('coBorrowerBirthDate')}
                type="date"
                label="Date de naissance"
                required
                error={fieldError('coBorrowerBirthDate')}
                onChange={handleFieldChange('coBorrowerBirthDate')}
                helperText="Âge minimum : 18 ans"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nationalité */}
                <div>
                  <SelectField
                    {...register('coBorrowerNationality')}
                    label="Nationalité"
                    options={getCountryOptions()}
                    required
                    error={fieldError('coBorrowerNationality')}
                    onChange={handleFieldChange('coBorrowerNationality')}
                  />
                </div>

                {/* Pays de naissance */}
                <div>
                  <SelectField
                    {...register('coBorrowerBirthCountry')}
                    label="Pays de naissance"
                    options={getCountryOptions()}
                    required
                    error={fieldError('coBorrowerBirthCountry')}
                    onChange={(e) => {
                      handleFieldChange('coBorrowerBirthCountry')(e);
                      setValue('coBorrowerBirthCity', '', { shouldValidate: true });
                      updateField('coBorrowerBirthCity', '');
                      setValue('coBorrowerBirthDepartment', '', { shouldValidate: true });
                      updateField('coBorrowerBirthDepartment', '');
                      setCoBorrowerBirthCityConfirmed(false);
                      setIsCoBorrowerBirthCountryFR(e.target.value === 'FR');
                    }}
                  />
                </div>
              </div>

              {/* Ville de naissance — uniquement si né en France */}
              {isCoBorrowerBirthCountryFR && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <CityAutocomplete
                      value={formData.coBorrowerBirthCity || ''}
                      onChange={(value) => {
                        setValue('coBorrowerBirthCity', value, { shouldValidate: true });
                        updateField('coBorrowerBirthCity', value);
                        updateField('coBorrowerBirthCityVerified', false);
                        setCoBorrowerBirthCityConfirmed(false);
                      }}
                      onDepartmentExtracted={(departmentCode) => {
                        setValue('coBorrowerBirthDepartment', departmentCode, { shouldValidate: true, shouldTouch: true });
                        setValue('coBorrowerBirthCity', watch('coBorrowerBirthCity'), { shouldTouch: true });
                        updateField('coBorrowerBirthDepartment', departmentCode);
                        updateField('coBorrowerBirthCityVerified', true);
                        setCoBorrowerBirthCityConfirmed(true);
                      }}
                      onSelectionCleared={() => {
                        updateField('coBorrowerBirthCityVerified', false);
                        setCoBorrowerBirthCityConfirmed(false);
                      }}
                      onBlur={touchField('coBorrowerBirthCity')}
                      label="Ville de naissance"
                      placeholder="Ville de naissance"
                      required
                      error={
                        touchedFields.coBorrowerBirthCity && !coBorrowerBirthCityConfirmed && (formData.coBorrowerBirthCity || '').length >= 2
                          ? 'Veuillez sélectionner votre ville dans la liste'
                          : fieldError('coBorrowerBirthCity')
                      }
                      helperText="Tapez votre ville et sélectionnez-la dans la liste"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 2: Contact */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-900 border-b pb-2">
                Contact
              </h3>

              <EmailInput
                value={formData.coBorrowerEmail || ''}
                onChange={(e) => {
                  setValue('coBorrowerEmail', e.target.value, { shouldValidate: true });
                  updateField('coBorrowerEmail', e.target.value);
                }}
                onBlur={touchField('coBorrowerEmail')}
                label="Adresse email"
                placeholder="votre@email.com"
                required
                error={fieldError('coBorrowerEmail')}
                helperText="Nous utiliserons cette adresse pour vous contacter"
              />

              <PhoneInput
                value={formData.coBorrowerPhone || ''}
                onChange={(value) => {
                  setValue('coBorrowerPhone', value, { shouldValidate: true, shouldTouch: true });
                  updateField('coBorrowerPhone', value);
                }}
                label="Numéro de téléphone"
                required
                error={fieldError('coBorrowerPhone')}
              />
            </div>

            {/* SECTION 3: Informations professionnelles */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-900 border-b pb-2">
                Informations professionnelles
              </h3>

              {/* Situation professionnelle — select avec optgroups (Annexe L) */}
              <div className="field-group">
                <label className="form-label">
                  Quelle est sa situation professionnelle ?
                  <span className="form-label-required">*</span>
                </label>
                <select
                  {...register('coBorrowerProfessionCode')}
                  className={`input-field ${touchedFields.coBorrowerProfessionCode && formErrors.coBorrowerProfessionCode ? 'input-error' : ''}`}
                  onChange={handleProfessionCodeChange}
                >
                  <option value="">-- Sélectionnez --</option>
                  <optgroup label="Indépendants">
                    <option value="1100">Artisans</option>
                    <option value="2100">Commerçants</option>
                    <option value="2200">Chefs d'entreprise</option>
                    <option value="2300">Professions agricoles indépendantes</option>
                    <option value="3100">Professions libérales</option>
                  </optgroup>
                  <optgroup label="Salariés">
                    <option value="3300">Cadres du privé</option>
                    <option value="3800">Professions intermédiaires du privé</option>
                    <option value="4500">Autres professions du public</option>
                    <option value="4600">Autres professions du privé</option>
                    <option value="5200">Employés du public</option>
                    <option value="5300">Employés du privé</option>
                    <option value="5400">Professions agricoles salariées</option>
                    <option value="5500">Personnels de maison</option>
                    <option value="5600">Apprentis, stagiaires</option>
                    <option value="6300">Ouvriers</option>
                  </optgroup>
                  <optgroup label="Inactifs">
                    <option value="7700">Retraités, pré-retraités</option>
                    <option value="8100">Demandeurs d'emploi</option>
                    <option value="8400">Étudiants</option>
                    <option value="8500">Autres inactifs</option>
                  </optgroup>
                </select>
                {touchedFields.coBorrowerProfessionCode && formErrors.coBorrowerProfessionCode && (
                  <p className="error-message">{formErrors.coBorrowerProfessionCode.message}</p>
                )}
              </div>

              {/* Type de contrat — uniquement pour salariés (3300–6300) */}
              {needsContractType && (
                <RadioGroup
                  label="Type de contrat"
                  options={contractTypeOptions}
                  value={watchedContractTypeCode}
                  onChange={(value) => {
                    setValue('coBorrowerContractTypeCode', value, { shouldValidate: true, shouldTouch: true });
                    updateField('coBorrowerContractTypeCode', value);
                  }}
                  name="coBorrowerContractTypeCode"
                  required
                  error={fieldError('coBorrowerContractTypeCode')}
                />
              )}

              {/* Revenus mensuels nets — uniquement pour les actifs (professionCode <= 6300) */}
              {isActiveProfession && (
                <CurrencyInput
                  value={watchedCoBorrowerMonthlySalary || 0}
                  onChange={(value) => {
                    setValue('coBorrowerMonthlySalary', value, { shouldValidate: true });
                    updateField('coBorrowerMonthlySalary', value);
                  }}
                  onBlur={touchField('coBorrowerMonthlySalary')}
                  label="Revenus mensuels nets"
                  placeholder="1 500 €"
                  required
                  error={fieldError('coBorrowerMonthlySalary')}
                  helperText="Indiquez les revenus nets mensuels en euros"
                  step={100}
                  showProgress={false}
                />
              )}

              {/* Ancienneté et employeur — uniquement pour les actifs */}
              {isActiveProfession && (
                <>
                  {/* Ancienneté et Nom de l'entreprise sur la même ligne */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Ancienneté (1/3) */}
                    <div className="sm:col-span-1">
                      <div className="field-group">
                        <label className="form-label">
                          Ancienneté
                        </label>
                        <div className="relative">
                          <input
                            {...register('coBorrowerSeniorityMonths', { valueAsNumber: true })}
                            type="number"
                            placeholder="24"
                            className="input-field pr-16"
                            onChange={(e) => {
                              if (e.target.value === '') {
                                setValue('coBorrowerSeniorityMonths', '' as unknown as number, { shouldValidate: true });
                                updateField('coBorrowerSeniorityMonths', '' as unknown as number);
                                return;
                              }
                              const capped = Math.min(Number(e.target.value), maxCoBorrowerSeniorityMonths);
                              e.target.value = String(capped);
                              setValue('coBorrowerSeniorityMonths', capped, { shouldValidate: true });
                              updateField('coBorrowerSeniorityMonths', capped);
                            }}
                            min={0}
                            max={maxCoBorrowerSeniorityMonths}
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-500">
                            mois
                          </div>
                        </div>
                        {(watchedCoBorrowerSeniorityMonths ?? 0) > 0 && (
                          <p className="helper-text">
                            ≈ {((watchedCoBorrowerSeniorityMonths ?? 0) / 12) < 1
                              ? 'Moins d\'1 an'
                              : `${Math.round((watchedCoBorrowerSeniorityMonths ?? 0) / 12 * 10) / 10} an${Math.round((watchedCoBorrowerSeniorityMonths ?? 0) / 12 * 10) / 10 >= 2 ? 's' : ''}`}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Nom de l'entreprise (2/3) — uniquement pour salariés (3300–6300) */}
                    {canHaveEmployer && (
                      <div className="sm:col-span-2">
                        <TextField
                          {...register('coBorrowerCompanyName')}
                          label="Employeur"
                          placeholder="Ex: SARL Martin & Associés"
                          error={fieldError('coBorrowerCompanyName')}
                          onChange={handleFieldChange('coBorrowerCompanyName')}
                        />
                      </div>
                    )}
                  </div>

                  {/* Ville de l'employeur — uniquement pour salariés (3300–6300) */}
                  {canHaveEmployer && (
                    <CityAutocomplete
                      value={formData.coBorrowerCompanyCity || ''}
                      onChange={(value) => {
                        setValue('coBorrowerCompanyCity', value, { shouldValidate: true });
                        updateField('coBorrowerCompanyCity', value);
                        setCoBorrowerCompanyCityConfirmed(false);
                      }}
                      onCitySelect={(data) => {
                        setValue('coBorrowerCompanyCity', data.city, { shouldValidate: true });
                        updateField('coBorrowerCompanyCity', data.city);
                        setCoBorrowerCompanyCityConfirmed(true);
                      }}
                      onSelectionCleared={() => setCoBorrowerCompanyCityConfirmed(false)}
                      label="Ville de l'employeur"
                      placeholder="Paris, Lyon..."
                      error={
                        !coBorrowerCompanyCityConfirmed && (formData.coBorrowerCompanyCity || '').length >= 2
                          ? 'Veuillez sélectionner la ville dans la liste'
                          : undefined
                      }
                    />
                  )}
                </>
              )}

              {/* Date de fin de contrat pour CDD/INT */}
              {['CDD', 'INT'].includes(watchedContractTypeCode) && (
                <TextField
                  {...register('coBorrowerContractEndDate')}
                  type="date"
                  label="Date de fin de contrat"
                  required
                  error={fieldError('coBorrowerContractEndDate')}
                  onChange={handleFieldChange('coBorrowerContractEndDate')}
                  helperText="Date de fin prévue du contrat"
                />
              )}

              {/* Pensions */}
              <div className="space-y-4 pt-4 border-t border-neutral-200">
                <h4 className="text-sm font-semibold text-neutral-700">
                  Pensions
                </h4>

                {/* Pension de retraite */}
                <div className="grid grid-cols-2 gap-4 items-end">
                  <CurrencyInput
                    value={watchedCoBorrowerRetirementPension || 0}
                    onChange={(value) => { setValue('coBorrowerRetirementPension', value, { shouldValidate: true }); updateField('coBorrowerRetirementPension', value); }}
                    label="Pension de retraite"
                    placeholder="1 000 €"
                    error={formErrors.coBorrowerRetirementPension?.message}
                    min={0} step={50} showProgress={false}
                  />
                  <div className="field-group">
                    <label className="form-label">Fréquence</label>
                    <select {...register('coBorrowerRetirementPensionPeriodicity')} className="input-field" onChange={handleFieldChange('coBorrowerRetirementPensionPeriodicity')}>
                      <option value="M">Mensuelle</option>
                      <option value="T">Trimestrielle</option>
                      <option value="S">Semestrielle</option>
                      <option value="A">Annuelle</option>
                    </select>
                  </div>
                </div>

                {/* Pension d'invalidité */}
                <div className="grid grid-cols-2 gap-4 items-end">
                  <CurrencyInput
                    value={watchedCoBorrowerDisabilityPension || 0}
                    onChange={(value) => { setValue('coBorrowerDisabilityPension', value, { shouldValidate: true }); updateField('coBorrowerDisabilityPension', value); }}
                    label="Pension d'invalidité"
                    placeholder="1 000 €"
                    error={formErrors.coBorrowerDisabilityPension?.message}
                    min={0} step={50} showProgress={false}
                  />
                  <div className="field-group">
                    <label className="form-label">Fréquence</label>
                    <select {...register('coBorrowerDisabilityPensionPeriodicity')} className="input-field" onChange={handleFieldChange('coBorrowerDisabilityPensionPeriodicity')}>
                      <option value="M">Mensuelle</option>
                      <option value="T">Trimestrielle</option>
                      <option value="S">Semestrielle</option>
                      <option value="A">Annuelle</option>
                    </select>
                  </div>
                </div>

                {/* Pension de réversion */}
                <div className="grid grid-cols-2 gap-4 items-end">
                  <CurrencyInput
                    value={watchedCoBorrowerReversionaryPension || 0}
                    onChange={(value) => { setValue('coBorrowerReversionaryPension', value, { shouldValidate: true }); updateField('coBorrowerReversionaryPension', value); }}
                    label="Pension de réversion"
                    placeholder="1 000 €"
                    error={formErrors.coBorrowerReversionaryPension?.message}
                    min={0} step={50} showProgress={false}
                  />
                  <div className="field-group">
                    <label className="form-label">Fréquence</label>
                    <select {...register('coBorrowerReversionaryPensionPeriodicity')} className="input-field" onChange={handleFieldChange('coBorrowerReversionaryPensionPeriodicity')}>
                      <option value="M">Mensuelle</option>
                      <option value="T">Trimestrielle</option>
                      <option value="S">Semestrielle</option>
                      <option value="A">Annuelle</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Autres revenus */}
              <div>
                <CurrencyInput
                  value={watchedCoBorrowerOtherIncome || 0}
                  onChange={(value) => { setValue('coBorrowerOtherIncome', value, { shouldValidate: true }); updateField('coBorrowerOtherIncome', value); }}
                  label="Autres revenus mensuels"
                  placeholder="1 000 €"
                  error={formErrors.coBorrowerOtherIncome?.message}
                  helperText="Allocations, revenus locatifs, etc."
                  min={0} step={50}
                />
              </div>
            </div>

            {/* Note informative */}
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <h3 className="font-semibold text-primary-800 mb-2">
                ℹ️ Pourquoi ces informations ?
              </h3>
              <p className="text-sm text-primary-700">
                Les informations du co-emprunteur sont nécessaires pour évaluer la capacité de remboursement globale du foyer.
                Tous les revenus du couple sont pris en compte dans l'analyse du dossier.
              </p>
            </div>

            <FormNavigation
              onPrevious={goToPreviousStep}
              onNext={handleSubmit(onSubmit)}
              canGoBack={canGoBack}
              canGoNext={isValid && (coBorrowerBirthCityConfirmed || !isCoBorrowerBirthCountryFR)}
              nextButtonText="Continuer"
            />
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
